import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserPlus, Search, Loader2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import ImportDialog from "@/components/ImportDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const INSURERS = ["Star Health", "HDFC Ergo", "ICICI Lombard", "Niva Bupa", "Care Health", "Bajaj Allianz", "New India Assurance", "United India", "Oriental Insurance", "Tata AIG", "Ayushman Bharat (PM-JAY)", "CGHS", "Other"];

export const EMPTY_PATIENT = { name: "", age: "", gender: "", phone: "", email: "", address: "", blood_group: "", allergies: "", notes: "", has_insurance: "no", insurance_company: "", payment_mode: "Cash", referred_by: "", referred_date: "", preferred_language: "English", assigned_doctor_id: "", lmp: "", edd: "", gravida: "", para: "", abha_id: "" };

function eddFromLmp(lmp) {
    if (!lmp) return "";
    const d = new Date(lmp);
    d.setDate(d.getDate() + 280);
    return d.toISOString().slice(0, 10);
}
function gaFromLmp(lmp) {
    if (!lmp) return "";
    const days = Math.floor((Date.now() - new Date(lmp)) / 86400000);
    return days < 0 ? "" : `${Math.floor(days / 7)}w${days % 7}d`;
}

export function PatientForm({ initial, onSubmit, loading, submitLabel, testPrefix }) {
    const [form, setForm] = useState(initial || EMPTY_PATIENT);
    const [doctors, setDoctors] = useState([]);
    useEffect(() => { api.get("/team").then(({ data }) => setDoctors(data)).catch(() => {}); }, []);
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    return (
        <form onSubmit={(e) => { e.preventDefault(); onSubmit({ ...form, age: form.age ? Number(form.age) : null }); }} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
                <Label>Full Name *</Label>
                <Input data-testid={`${testPrefix}-name-input`} required value={form.name} onChange={set("name")} placeholder="Ramesh Kumar" />
            </div>
            <div className="space-y-1.5">
                <Label>Phone *</Label>
                <Input data-testid={`${testPrefix}-phone-input`} required value={form.phone} onChange={set("phone")} placeholder="9876543210" />
            </div>
            <div className="space-y-1.5">
                <Label>Age</Label>
                <Input data-testid={`${testPrefix}-age-input`} type="number" min="0" max="120" value={form.age} onChange={set("age")} placeholder="35" />
            </div>
            <div className="space-y-1.5">
                <Label>Gender</Label>
                <select data-testid={`${testPrefix}-gender-select`} value={form.gender} onChange={set("gender")} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3.5 text-sm shadow-sm focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none">
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                </select>
            </div>
            <div className="space-y-1.5">
                <Label>Blood Group</Label>
                <select data-testid={`${testPrefix}-blood-group-select`} value={form.blood_group} onChange={set("blood_group")} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3.5 text-sm shadow-sm focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none">
                    <option value="">Select</option>
                    {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
            </div>
            <div className="space-y-1.5">
                <Label>Email</Label>
                <Input data-testid={`${testPrefix}-email-input`} type="email" value={form.email} onChange={set("email")} placeholder="Optional" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
                <Label>ABHA Health ID</Label>
                <Input data-testid={`${testPrefix}-abha-input`} value={form.abha_id || ""} onChange={set("abha_id")} placeholder="12-3456-7890-1234" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
                <Label>Address</Label>
                <Input data-testid={`${testPrefix}-address-input`} value={form.address} onChange={set("address")} placeholder="House, Street, City" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
                <Label>Allergies</Label>
                <Input data-testid={`${testPrefix}-allergies-input`} value={form.allergies} onChange={set("allergies")} placeholder="e.g. Penicillin, Sulfa drugs" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
                <Label>Notes / Medical History</Label>
                <Input data-testid={`${testPrefix}-notes-input`} value={form.notes} onChange={set("notes")} placeholder="Diabetic since 2018, hypertensive..." />
            </div>
            <div className="space-y-1.5">
                <Label>Referred By</Label>
                <Input data-testid={`${testPrefix}-referred-by-input`} value={form.referred_by} onChange={set("referred_by")} placeholder="Dr. name / self / camp" />
            </div>
            <div className="space-y-1.5">
                <Label>Reference Date</Label>
                <Input data-testid={`${testPrefix}-referred-date-input`} type="date" value={form.referred_date} onChange={set("referred_date")} />
            </div>
            <div className="space-y-1.5">
                <Label>Preferred Language</Label>
                <select data-testid={`${testPrefix}-language-select`} value={form.preferred_language} onChange={set("preferred_language")} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3.5 text-sm shadow-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none">
                    {["English", "Hindi", "Gujarati"].map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
            </div>
            <div className="space-y-1.5">
                <Label>Assigned Doctor</Label>
                <select data-testid={`${testPrefix}-doctor-select`} value={form.assigned_doctor_id || ""} onChange={set("assigned_doctor_id")} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3.5 text-sm shadow-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none">
                    <option value="">Unassigned</option>
                    {doctors.map((d) => <option key={d.id} value={d.id}>{d.name?.toLowerCase().startsWith("dr") ? d.name : `Dr. ${d.name}`}{d.specialization ? ` — ${d.specialization}` : ""}</option>)}
                </select>
            </div>
            <div className="space-y-1.5">
                <Label>LMP (Last Menstrual Period)</Label>
                <Input data-testid={`${testPrefix}-lmp-input`} type="date" value={form.lmp || ""} onChange={set("lmp")} max={new Date().toISOString().slice(0, 10)} />
                {form.lmp && <p className="text-[11px] font-medium text-[#C2410C]" data-testid={`${testPrefix}-edd-preview`}>EDD: {eddFromLmp(form.lmp)} · GA today: {gaFromLmp(form.lmp)}</p>}
            </div>
            <div className="space-y-1.5">
                <Label>Gravida / Para</Label>
                <div className="flex gap-2">
                    <Input data-testid={`${testPrefix}-gravida-input`} value={form.gravida || ""} onChange={set("gravida")} placeholder="G2" />
                    <Input data-testid={`${testPrefix}-para-input`} value={form.para || ""} onChange={set("para")} placeholder="P1" />
                </div>
            </div>
            <div className="space-y-1.5">
                <Label>Medical Policy (Insurance)?</Label>
                <select data-testid={`${testPrefix}-insurance-select`} value={form.has_insurance} onChange={set("has_insurance")} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3.5 text-sm shadow-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none">
                    <option value="no">No (Cash)</option>
                    <option value="yes">Yes</option>
                </select>
            </div>
            {form.has_insurance === "yes" && (
                <>
                    <div className="space-y-1.5">
                        <Label>Insurance Company</Label>
                        <select data-testid={`${testPrefix}-insurance-company-select`} value={form.insurance_company} onChange={set("insurance_company")} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3.5 text-sm shadow-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none">
                            <option value="">Select company</option>
                            {INSURERS.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Payment Mode</Label>
                        <select data-testid={`${testPrefix}-payment-mode-select`} value={form.payment_mode} onChange={set("payment_mode")} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3.5 text-sm shadow-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none">
                            <option value="Medical Policy">Medical Policy</option>
                            <option value="Cash">Cash</option>
                        </select>
                    </div>
                </>
            )}
            <Button data-testid={`${testPrefix}-submit-btn`} type="submit" disabled={loading} className="sm:col-span-2 bg-[#E06D1F] hover:bg-[#B45309] text-white">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {submitLabel}
            </Button>
        </form>
    );
}

export default function Patients() {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const [patients, setPatients] = useState(null);
    const [search, setSearch] = useState("");
    const [showAdd, setShowAdd] = useState(params.get("new") === "1");
    const [saving, setSaving] = useState(false);

    const load = async (q = "") => {
        const { data } = await api.get("/patients", { params: { search: q } });
        setPatients(data);
    };

    useEffect(() => {
        const t = setTimeout(() => load(search), 250);
        return () => clearTimeout(t);
    }, [search]);

    const addPatient = async (payload) => {
        setSaving(true);
        try {
            const { data } = await api.post("/patients", payload);
            toast.success(`Patient ${data.name} added (${data.patient_uid})`);
            setShowAdd(false);
            setParams({});
            navigate(`/patients/${data.id}`);
        } catch (e) {
            toast.error(fmtErr(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-5 animate-fade-up" data-testid="patients-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Patients</h1>
                    <p className="mt-1 text-base text-slate-500">{patients ? `${patients.length} record${patients.length === 1 ? "" : "s"}` : "Loading..."}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <ImportDialog module="patients" label="Import" onDone={() => load()} />
                    <ImportDialog module="documents" label="Import Docs" />
                    <Button data-testid="add-patient-btn" onClick={() => setShowAdd(true)} className="bg-[#E06D1F] hover:bg-[#B45309] text-white gap-2">
                        <UserPlus className="h-4 w-4" /> Add Patient
                    </Button>
                </div>
            </div>

            <div className="relative max-w-xl">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                    data-testid="patient-search-input"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name, phone or patient ID..."
                    className="w-full rounded-xl border border-slate-300 bg-white py-3 pl-10 pr-3.5 text-sm shadow-sm focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none placeholder:text-slate-400"
                />
            </div>

            <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="patients-table-card">
                {!patients ? (
                    <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#E06D1F]" /></div>
                ) : patients.length === 0 ? (
                    <p className="px-5 py-12 text-center text-sm text-slate-500" data-testid="patients-empty">No patients found.</p>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
                                <th className="px-5 py-3">Patient ID</th>
                                <th className="px-5 py-3">Name</th>
                                <th className="px-5 py-3 hidden sm:table-cell">Age/Gender</th>
                                <th className="px-5 py-3 hidden md:table-cell">Phone</th>
                                <th className="px-5 py-3 hidden lg:table-cell">Last Visit</th>
                                <th className="px-5 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {patients.map((p) => (
                                <tr key={p.id} data-testid={`patient-row-${p.patient_uid}`} onClick={() => navigate(`/patients/${p.id}`)} className="border-t border-slate-100 hover:bg-[#FFF4EC]/60 cursor-pointer transition-colors duration-100">
                                    <td className="px-5 py-3 font-mono2 text-xs text-[#C2410C] font-medium">{p.patient_uid}</td>
                                    <td className="px-5 py-3 font-medium text-slate-900">{p.name}</td>
                                    <td className="px-5 py-3 hidden sm:table-cell text-slate-600">{p.age ? `${p.age} yrs` : "-"} / {p.gender || "-"}</td>
                                    <td className="px-5 py-3 hidden md:table-cell text-slate-600">{p.phone}</td>
                                    <td className="px-5 py-3 hidden lg:table-cell text-slate-600">{p.last_visit || "—"}</td>
                                    <td className="px-5 py-3 text-right"><ChevronRight className="h-4 w-4 text-slate-300 inline" /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <Dialog open={showAdd} onOpenChange={(o) => { setShowAdd(o); if (!o) setParams({}); }}>
                <DialogContent className="max-w-2xl" data-testid="add-patient-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Add New Patient</DialogTitle>
                        <DialogDescription>Enter the patient's details. Name and phone are required.</DialogDescription>
                    </DialogHeader>
                    <PatientForm testPrefix="add-patient" submitLabel="Save Patient" loading={saving} onSubmit={addPatient} />
                </DialogContent>
            </Dialog>
        </div>
    );
}
